import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
// #region --------------- Contact React Query hooks ----------------------

import {
  createMatterTeam,
  createMatterTeamConnection,
  deleteMatterTeam,
  deleteMatterTeamConnection,
  updateMatterTeam,
  updateMatterTeamConnection,
  type MatterSettings,
} from "@/lib/data/requests";
import { queryKeys } from "@/lib/data/static";
import { type APIError, type APIResponse } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type AxiosError } from "axios";
import { orderBy } from "lodash";
import toast from "react-hot-toast";

export const useCreateMatterTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterTeam>[0]) =>
      createMatterTeam(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              teams: orderBy(
                [...oldData.data.teams, { ...data, id: "temp" }],
                ["label"],
                ["asc"],
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error creating matter team");
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      toast.success("Matter team created successfully");
    },
  });
};

export const useUpdateMatterTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterTeam>[0]) =>
      updateMatterTeam(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;

          return {
            ...oldData,
            data: {
              ...oldData.data,
              teams: orderBy(
                oldData.data.teams.map((group) =>
                  group.id === data.id ? { ...data, id: data.id } : group,
                ),
                ["label"],
                ["asc"],
              ),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error updating matter team");
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      toast.success("Matter team updated successfully");
    },
  });
};

export const useDeleteMatterTeam = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterTeam>[0]) =>
      deleteMatterTeam(data),
    onMutate: (data) => {
      // Optimistic update
      queryClient.setQueryData(
        [queryKeys.matterSettings],
        (oldData: APIResponse<MatterSettings> | undefined) => {
          if (!oldData) return;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              teams: oldData.data.teams.filter((group) => group.id !== data.id),
            },
          };
        },
      );

      return {
        previousData: queryClient.getQueryData([queryKeys.matterSettings]),
      };
    },
    onError: (error: AxiosError<APIError>, _variables, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(
          [queryKeys.matterSettings],
          context.previousData,
        );
      }
      toastAPIErrorMessage(error, "Error deleting matter team");
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matterSettings],
      });
    },
    onSuccess: () => {
      toast.success("Matter team deleted successfully");
    },
  });
};

export const useCreateMatterTeamConnection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof createMatterTeamConnection>[0]) =>
      createMatterTeamConnection(data),
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error creating matter team connection");
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Matter team connection created successfully");
    },
  });
};

export const useUpdateMatterTeamConnectionEndTime = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof updateMatterTeamConnection>[0]) =>
      updateMatterTeamConnection(data),
    onError: (err: AxiosError<APIError>) => {
      toastAPIErrorMessage(err, "Error updating matter team connection");
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Matter team connection updated successfully");
    },
  });
};

export const useDeleteMatterTeamConnection = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof deleteMatterTeamConnection>[0]) =>
      deleteMatterTeamConnection(data),
    onError: () => {
      toast.error("Error deleting matter team connection");
    },

    onSettled: async () => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters],
      });
    },
    onSuccess: () => {
      toast.success("Matter team connection deleted successfully");
    },
  });
};
